import React, { useState, useCallback } from "react";
import { useBulkEditContext, BulkEditRow, CellState } from "../../store/bulk-edit-store";
import { BULK_EDIT_IDENTIFIER_COLUMNS, BULK_EDIT_STICKY_COLUMNS, BULK_EDIT_OPTIONAL_IDENTIFIER_COLUMNS } from "./BulkEditConstants";
import { formatProjectFields } from "../activeProjects/ActiveProjectsUtils";
import { validateCell } from "./BulkEditValidation";
import { recalculateFormulas, isFormulaInput } from "./BulkEditFormulaEngine";
import BulkEditCell from "./BulkEditCell";
import cloneDeep from "lodash/cloneDeep";
import "./BulkEditTable.scss";
import "./BulkEditCell.scss";

export default function BulkEditTable() {
  const { rows, setRows, visibleColumns, currentPage, pageSize, cellStates, setCellStates, setHasUnsavedChanges } = useBulkEditContext();

  // Track active cell: { rowIndex, colIndex } relative to visible rows/columns
  const [activeCell, setActiveCell] = useState<{ rowIndex: number; colIndex: number } | null>(null);
  // Track validation errors: key is `${projectId}::${fieldName}`
  const [validationErrors, setValidationErrors] = useState<Record<string, string | null>>({});

  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const visibleRows = rows.slice(startIndex, endIndex);

  const handleValueChange = useCallback((projectId: string, fieldName: string, newValue: string) => {
    const updatedRows = cloneDeep(rows);
    const rowIndex = updatedRows.findIndex((r) => r.projectId === projectId);
    if (rowIndex === -1) return;

    const fieldData = updatedRows[rowIndex].editableFields[fieldName];
    const cellKey = `${projectId}::${fieldName}`;
    const newCellStates = { ...cellStates };
    const newValidationErrors = { ...validationErrors };

    // Validate the new value
    const validation = validateCell(newValue, fieldName, fieldData);

    if (!validation.isValid) {
      // Invalid: always show the invalid flag with error tooltip,
      // regardless of whether the cell value equals original.
      // The save manager already excludes unchanged cells from the modified count.
      newCellStates[cellKey] = "invalid";
      newValidationErrors[cellKey] = validation.errorMessage;
      setCellStates(newCellStates);
      setValidationErrors(newValidationErrors);
      return;
    }

    // Valid: update the value
    updatedRows[rowIndex].editableFields[fieldName].value = newValue;
    newValidationErrors[cellKey] = null;

    // Determine cell state
    const originalValue = fieldData.originalValue;
    if (newValue !== originalValue) {
      newCellStates[cellKey] = "valid-modified";
      setHasUnsavedChanges(true);
    } else {
      newCellStates[cellKey] = "pristine";
    }

    // Run formula recalculation if this field is a formula input
    if (isFormulaInput(fieldName)) {
      const { updatedFields, errors } = recalculateFormulas(fieldName, updatedRows[rowIndex]);

      for (const [resultField, resultValue] of Object.entries(updatedFields)) {
        if (updatedRows[rowIndex].editableFields[resultField]) {
          updatedRows[rowIndex].editableFields[resultField].value = resultValue;
          const resultCellKey = `${projectId}::${resultField}`;
          newCellStates[resultCellKey] = "formula-updated";
          newValidationErrors[resultCellKey] = null;
        }
      }

      for (const [resultField, errorMsg] of Object.entries(errors)) {
        const resultCellKey = `${projectId}::${resultField}`;
        newCellStates[resultCellKey] = "invalid";
        newValidationErrors[resultCellKey] = errorMsg;
      }
    }

    setRows(updatedRows);
    setCellStates(newCellStates);
    setValidationErrors(newValidationErrors);
  }, [rows, cellStates, validationErrors, setRows, setCellStates, setHasUnsavedChanges]);

  const editableVisibleColumns = visibleColumns.filter((col) => !BULK_EDIT_OPTIONAL_IDENTIFIER_COLUMNS.includes(col as any));

  const handleNavigate = useCallback((direction: "tab" | "enter") => {
    if (!activeCell) return;

    const { rowIndex, colIndex } = activeCell;

    if (direction === "tab") {
      // Move to next column, wrap to next row
      if (colIndex < editableVisibleColumns.length - 1) {
        setActiveCell({ rowIndex, colIndex: colIndex + 1 });
      } else if (rowIndex < visibleRows.length - 1) {
        setActiveCell({ rowIndex: rowIndex + 1, colIndex: 0 });
      } else {
        setActiveCell(null);
      }
    } else if (direction === "enter") {
      // Move to cell below in same column
      if (rowIndex < visibleRows.length - 1) {
        setActiveCell({ rowIndex: rowIndex + 1, colIndex });
      } else {
        setActiveCell(null);
      }
    }
  }, [activeCell, editableVisibleColumns.length, visibleRows.length]);

  const getCellState = (projectId: string, fieldName: string): CellState => {
    const cellKey = `${projectId}::${fieldName}`;
    return cellStates[cellKey] || "pristine";
  };

  if (rows.length === 0) {
    return <div className="bulk-edit-empty">No projects loaded.</div>;
  }

  if (visibleColumns.length === 0) {
    return <div className="bulk-edit-empty">No editable columns selected. Use the Column Filter to select at least one column.</div>;
  }

  return (
    <div className="bulk-edit-table-container">
      <table className="bulk-edit-table">
        <thead>
          <tr className="bulk-edit-header-row">
            {BULK_EDIT_IDENTIFIER_COLUMNS.map((colName, index) => {
              const isSticky = BULK_EDIT_STICKY_COLUMNS.includes(colName as any);
              return (
                <th
                  key={`id-header-${colName}`}
                  className={`bulk-edit-header-cell bulk-edit-identifier-header${isSticky ? " bulk-edit-sticky-col" : ""}`}
                  style={isSticky ? { left: `${index * 120}px` } : undefined}
                >
                  {colName}
                </th>
              );
            })}
            {BULK_EDIT_OPTIONAL_IDENTIFIER_COLUMNS.filter((col) => visibleColumns.includes(col)).map((colName) => (
              <th key={`opt-id-header-${colName}`} className="bulk-edit-header-cell bulk-edit-identifier-header">
                {colName}
              </th>
            ))}
            {visibleColumns.filter((col) => !BULK_EDIT_OPTIONAL_IDENTIFIER_COLUMNS.includes(col as any)).map((colName) => (
              <th key={`edit-header-${colName}`} className="bulk-edit-header-cell bulk-edit-editable-header">
                {colName}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {visibleRows.map((row, rowIndex) => (
            <tr key={row.projectId} className="bulk-edit-row">
              {/* Identifier cells (read-only, grey — only first 3 are sticky) */}
              {BULK_EDIT_IDENTIFIER_COLUMNS.map((colName, index) => {
                const isSticky = BULK_EDIT_STICKY_COLUMNS.includes(colName as any);
                return (
                  <td
                    key={`${row.projectId}-id-${colName}`}
                    className={`bulk-edit-cell bulk-edit-identifier-cell${isSticky ? " bulk-edit-sticky-col" : ""}`}
                    style={isSticky ? { left: `${index * 120}px` } : undefined}
                  >
                    {row.identifierFields[colName] || "—"}
                  </td>
                );
              })}
              {/* Optional identifier cells (read-only, toggleable) */}
              {BULK_EDIT_OPTIONAL_IDENTIFIER_COLUMNS.filter((col) => visibleColumns.includes(col)).map((colName) => (
                <td
                  key={`${row.projectId}-opt-id-${colName}`}
                  className="bulk-edit-cell bulk-edit-identifier-cell"
                >
                  {row.identifierFields[colName] || "—"}
                </td>
              ))}
              {/* Editable cells */}
              {visibleColumns.filter((col) => !BULK_EDIT_OPTIONAL_IDENTIFIER_COLUMNS.includes(col as any)).map((colName, colIndex) => {
                const fieldData = row.editableFields[colName];
                if (!fieldData) {
                  return (
                    <td key={`${row.projectId}-edit-${colName}`} className="bulk-edit-cell bulk-edit-editable-cell">
                      —
                    </td>
                  );
                }
                const isActive = activeCell?.rowIndex === rowIndex && activeCell?.colIndex === colIndex;
                return (
                  <BulkEditCell
                    key={`${row.projectId}-edit-${colName}`}
                    projectId={row.projectId}
                    fieldName={colName}
                    fieldData={fieldData}
                    cellState={getCellState(row.projectId, colName)}
                    validationError={validationErrors[`${row.projectId}::${colName}`] || null}
                    onValueChange={handleValueChange}
                    onNavigate={handleNavigate}
                    isActive={isActive}
                    onActivate={() => setActiveCell({ rowIndex, colIndex })}
                  />
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
